if (!customElements.get('product-form')) {
  const productForms = new Set();
  let cartVariantIds = null;
  let cartRequest;
  let cartRevision = 0;

  const updateCartMembership = (cart) => {
    cartVariantIds = new Set(cart.items.filter((item) => item.quantity > 0).map((item) => String(item.variant_id)));
    productForms.forEach((form) => form.updateAddedFeedback());
  };

  const refreshCartMembership = () => {
    if (cartRequest) return cartRequest;
    const revision = cartRevision;
    const request = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to read cart membership');
        return response.json();
      })
      .then((cart) => {
        if (revision === cartRevision) updateCartMembership(cart);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (cartRequest === request) cartRequest = null;
      });
    cartRequest = request;
    return request;
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    cartRevision += 1;
    cartRequest = null;
    if (Array.isArray(event.cartData?.items) && typeof event.cartData.item_count === 'number') {
      updateCartMembership(event.cartData);
      return;
    }
    return refreshCartMembership();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    cartRevision += 1;
    cartRequest = null;
    refreshCartMembership();
  });

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        productForms.add(this);
        if (cartVariantIds) this.updateAddedFeedback();
        else refreshCartMembership();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.resetAddedFeedback();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const productVariantId = formData.get('id');
        const giftWrap = this.form.querySelector('cart-gift-wrap[data-context="product"]');
        giftWrap?.setSubmitting(true);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        Promise.resolve()
          .then(() => giftWrap?.prepareProductAdd(formData))
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              // A multi-item error can concern the wrap, rather than the product's availability.
              if (formData.has('items[1][id]')) return;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (response.items) {
              const productItem = response.items.find((item) => String(item.variant_id || item.id) === String(productVariantId));
              if (productItem) response = { ...response, ...productItem };
            }
            if (cartVariantIds) cartVariantIds.add(String(productVariantId));
            this.showAddedFeedback(productVariantId);
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            if (giftWrap) this.handleErrorMessage(giftWrap.dataset.error);
          })
          .finally(() => {
            giftWrap?.setSubmitting(false);
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            if (!this.error) this.updateAddedFeedback();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      showAddedFeedback(variantId) {
        if (!this.isConnected || this.variantIdInput.value !== String(variantId)) return;
        this.resetAddedFeedback();
        this.originalButtonLabel = this.submitButtonText.textContent;
        this.addedButtonLabel = `✓ ${window.variantStrings.addedToCart}`;
        this.submitButtonText.textContent = this.addedButtonLabel;
      }

      updateAddedFeedback() {
        if (!cartVariantIds || !this.isConnected) return;
        this.resetAddedFeedback();
        if (!this.submitButton.disabled && !this.error && cartVariantIds.has(this.variantIdInput.value)) {
          this.showAddedFeedback(this.variantIdInput.value);
        }
      }

      resetAddedFeedback() {
        if (this.addedButtonLabel && this.submitButtonText.textContent === this.addedButtonLabel) {
          this.submitButtonText.textContent = this.originalButtonLabel;
        }
        this.addedButtonLabel = null;
      }

      disconnectedCallback() {
        productForms.delete(this);
        this.resetAddedFeedback();
      }

      toggleSubmitButton(disable = true, text) {
        this.resetAddedFeedback();
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.updateAddedFeedback();
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
